interface ErrorInfo {
  code: string;
  detail: string;
}

const ERROR_TITLES: Record<string, string> = {
  no_evidence: "No supporting evidence found",
  no_evidence_after_retry: "No supporting evidence after retry",
  synthesis_failed: "Couldn't synthesize an answer",
  tool_failure: "Retrieval tool error",
  rate_limited: "Rate limited",
  upstream_timeout: "Upstream model timed out",
  unauthorized: "Authentication failed",
  forbidden: "Access denied",
};

function errorTitleFor(code: string): string {
  return ERROR_TITLES[code] ?? "Something went wrong";
}

export function ErrorCard({ error }: { error: ErrorInfo }) {
  return (
    <div
      role="alert"
      className="type-body-sm mt-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--status-error)] bg-[var(--status-error-bg)] p-[var(--space-3)] text-[var(--status-error)]"
    >
      <div className="flex items-start gap-[var(--space-2)]">
        <span
          aria-hidden="true"
          className="type-caption mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--status-error)] font-bold text-[var(--text-on-accent)]"
        >
          !
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{errorTitleFor(error.code)}</div>
          {error.detail && error.detail !== error.code && (
            <div className="mt-[var(--space-1)] text-[var(--text-secondary)]">{error.detail}</div>
          )}
          <div className="type-caption mt-[var(--space-1)] font-mono text-[var(--text-tertiary)]">code: {error.code}</div>
        </div>
      </div>
    </div>
  );
}

interface TerminatedTone {
  bg: string;
  border: string;
  text: string;
  iconBg: string;
  label: string;
}

const TERMINATED_TONES: Record<string, TerminatedTone> = {
  cancelled: {
    bg: "bg-[var(--surface-sunken)]",
    border: "border-[var(--border-strong)]",
    text: "text-[var(--text-secondary)]",
    iconBg: "bg-[var(--text-tertiary)]",
    label: "Cancelled",
  },
  duration_cap: {
    bg: "bg-[var(--status-warning-bg)]",
    border: "border-[var(--status-warning)]",
    text: "text-[var(--text-primary)]",
    iconBg: "bg-[var(--status-warning)]",
    label: "Reached duration cap",
  },
  partial_coverage: {
    bg: "bg-[var(--status-warning-bg)]",
    border: "border-[var(--status-warning)]",
    text: "text-[var(--text-primary)]",
    iconBg: "bg-[var(--status-warning)]",
    label: "Partial coverage — some facets uncovered",
  },
  no_evidence: {
    bg: "bg-[var(--surface-sunken)]",
    border: "border-[var(--border-strong)]",
    text: "text-[var(--text-secondary)]",
    iconBg: "bg-[var(--text-tertiary)]",
    label: "No supporting evidence found",
  },
  error: {
    bg: "bg-[var(--status-error-bg)]",
    border: "border-[var(--status-error)]",
    text: "text-[var(--status-error)]",
    iconBg: "bg-[var(--status-error)]",
    label: "Error",
  },
  dead_lettered: {
    bg: "bg-[var(--status-error-bg)]",
    border: "border-[var(--status-error)]",
    text: "text-[var(--status-error)]",
    iconBg: "bg-[var(--status-error)]",
    label: "Failed permanently",
  },
};

const TERMINATED_HIDE = new Set(["completed", "cache_hit"]);

export function TerminatedNotice({ reason }: { reason: string }) {
  if (TERMINATED_HIDE.has(reason)) return null;
  const tone =
    TERMINATED_TONES[reason] ?? {
      bg: "bg-[var(--status-warning-bg)]",
      border: "border-[var(--status-warning)]",
      text: "text-[var(--text-primary)]",
      iconBg: "bg-[var(--status-warning)]",
      label: "Run terminated",
    };
  return (
    <div
      role="status"
      className={`type-body-sm mt-[var(--space-3)] flex items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] border ${tone.border} ${tone.bg} px-[var(--space-3)] py-[var(--space-2)] ${tone.text}`}
    >
      <span
        aria-hidden="true"
        className={`type-caption inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-[var(--radius-pill)] ${tone.iconBg} font-bold text-[var(--text-on-accent)]`}
      >
        !
      </span>
      <span className="font-medium">{tone.label}</span>
      <span className="type-caption font-mono text-[var(--text-tertiary)] opacity-70">{reason}</span>
    </div>
  );
}
